import * as React from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertTriangle, CheckCircle2, Circle, Loader2, RefreshCw } from 'lucide-react';
import { AUDIT_LABELS, auditProblemCount, type AuditCounts } from '@/lib/winnerAudit';

/**
 * What is wrong with this schedule, in words a director can act on.
 *
 * One way in — the error counter on the board's control row — and one source:
 * the publish check the server already runs (_shared/schedule-invariants.ts).
 * Nothing here recomputes a finding or rewords one; the sentences arrive
 * written and this only arranges them: errors first because they block
 * publishing, warnings after because they do not, each kind grouped with its
 * own count, every row a link to the cell it is about.
 */

export type CheckRow = {
  game_id?: string | null;
  game_number?: number | null;
  label?: string | null;
  division?: string | null;
  teams?: string | null;
  court?: string | null;
  date?: string | null;
  time?: string | null;
  when?: string | null;
};

type Props = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  check: any;
  checking?: boolean;
  onRunCheck: () => void;
  /** Scroll the board to this game and ring it. */
  onFocusGame: (row: CheckRow) => void;
  /** Filter the board to one team — for findings about a team, not a cell. */
  onFocusTeam?: (team: { registration_id?: string | null; team?: string | null }) => void;
  /** Opens the page's own "publish with unresolved problems" confirmation. */
  onPublishAnyway?: () => void;
  /** How many cells on the board carry a colour — the counter's own number. */
  cellCount?: number;
  /**
   * Which set that number counted. The Grid on one day counts that day and
   * says so; the Grid on All days and Bulk edit count the whole board.
   */
  scopeLabel?: string | null;
  /** Coloured cells outside the counted set, so the two numbers never argue. */
  offScopeCount?: number;
  /** Where a team's own page lives, given its registration id. */
  teamSummaryHref?: (registrationId: string) => string;
  /** The Format tab, where a turned-off check gets its number. */
  setupHref?: string;
  /** A named header count can open the panel directly at this group. */
  focusKind?: string | null;
  /** Sign off one team's back-to-back pair; undo from the approved list. */
  onApproveBackToBack?: (item: any) => Promise<void> | void;
  onUnapproveBackToBack?: (item: any) => Promise<void> | void;
  /** Mark every game in one finding on the board; same key again clears it. */
  onMarkFinding?: (key: string, title: string, rows: CheckRow[]) => void;
  markedKey?: string | null;
};

/** "16u Game 4 · Court B · Sat 14 Nov 10:15am" — what a row says on the panel. */
export function rowText(r: CheckRow): string {
  const where = [r.court, r.when ?? [r.date, r.time].filter(Boolean).join(' ')].filter(Boolean).join(' · ');
  const head = r.label || (r.game_number != null ? `Game ${r.game_number}` : null)
    || [r.division, r.teams].filter(Boolean).join(' ');
  return [head, r.teams && head !== r.teams ? r.teams : null, where].filter(Boolean).join(' · ');
}

const plural = (n: number, one: string, many = `${one}s`) => (n === 1 ? one : many);

/** Errors carry one kind each; the panel shows one group per kind. */
function groupErrors(errors: any[]): Array<{ kind: string; title: string; items: any[] }> {
  const order: string[] = [];
  const by = new Map<string, { kind: string; title: string; items: any[] }>();
  for (const e of errors ?? []) {
    const kind = String(e?.kind ?? 'problem');
    if (!by.has(kind)) { by.set(kind, { kind, title: e?.title ?? 'A problem', items: [] }); order.push(kind); }
    by.get(kind)!.items.push(e);
  }
  return order.map((k) => by.get(k)!);
}

export default function ScheduleCheckPanel({
  open, onOpenChange, check, checking = false, onRunCheck, onFocusGame, onFocusTeam,
  onPublishAnyway, cellCount = 0, scopeLabel = null, offScopeCount = 0, teamSummaryHref,
  setupHref, focusKind = null, onApproveBackToBack, onUnapproveBackToBack,
  onMarkFinding, markedKey = null,
}: Props) {
  const [b2bBusy, setB2bBusy] = React.useState<string | null>(null);
  const approvedB2B: any[] = Array.isArray(check?.approved_back_to_back) ? check.approved_back_to_back : [];
  const runB2B = async (key: string, fn?: (it: any) => any, it?: any) => {
    if (!fn) return;
    setB2bBusy(key);
    try { await fn(it); } finally { setB2bBusy(null); }
  };
  const uncheckedRows: any[] = Array.isArray(check?.unchecked) ? check.unchecked : [];
  const errorCount = check?.error_count ?? 0;
  const warningCount = check?.warning_count ?? 0;
  const audit: AuditCounts | null = check?.integrity_audit ?? null;
  const errorGroups = React.useMemo(() => groupErrors(check?.errors ?? []), [check]);
  const warningGroups: any[] = Array.isArray(check?.warnings) ? check.warnings : [];

  const go = (row: CheckRow) => { if (row?.game_id) { onFocusGame(row); onOpenChange(false); } };
  const goTeam = (t: any) => { if (onFocusTeam) { onFocusTeam(t); onOpenChange(false); } };

  // 'errors' / 'warnings' / a warning kind filter the list to what was clicked.
  const [filter, setFilter] = React.useState<string | null>(null);
  React.useEffect(() => { if (open) setFilter(focusKind); }, [open, focusKind]);
  const showErrors = !filter || filter === 'errors';
  const shownWarnings = !filter ? warningGroups
    : filter === 'errors' ? [] : filter === 'warnings' ? warningGroups
    : warningGroups.filter((w: any) => w?.kind === filter);

  /** The finding's sentence, as a button that marks its games on the board. */
  const Finding = ({ fkey, title, text, finding }: { fkey: string; title: string; text: string; finding: any }) => {
    const rows: CheckRow[] = (Array.isArray(finding?.games) ? finding.games : finding?.game_id ? [finding] : [])
      .filter((r: any) => r?.game_id);
    if (!onMarkFinding || !rows.length) return <div className="text-foreground">{text}</div>;
    const on = markedKey === fkey;
    return (
      <button
        type="button"
        data-testid="check-finding"
        aria-pressed={on}
        onClick={() => { onMarkFinding(fkey, title, rows); onOpenChange(false); }}
        className={`w-full rounded px-1 py-0.5 text-left text-foreground hover:bg-muted ${on ? 'bg-muted font-medium ring-2 ring-foreground' : ''}`}
      >
        {text} <span className="whitespace-nowrap font-semibold underline">— show {rows.length} game{rows.length === 1 ? '' : 's'} on the board</span>
      </button>
    );
  };

  React.useEffect(() => {
    if (!open || !focusKind || focusKind === 'errors' || focusKind === 'warnings') return;
    const timer = window.setTimeout(() => {
      document.querySelector(`[data-warning-kind="${focusKind}"]`)
        ?.scrollIntoView({ block: 'start' });
    }, 80);
    return () => window.clearTimeout(timer);
  }, [open, focusKind]);

  /**
   * The chips under a sentence. A finding about games links to its cells; a
   * finding about a team links to the team, since there is no one cell to
   * scroll to; a finding about the event has neither and draws nothing.
   */
  const Chips = ({ finding }: { finding: any }) => {
    const games: CheckRow[] = Array.isArray(finding?.games) ? finding.games : [];
    const teams: any[] = Array.isArray(finding?.teams_focus) ? finding.teams_focus : [];
    if (!games.length && !teams.length) return null;
    return (
      <div className="mt-1 flex flex-wrap gap-1.5">
        {games.map((row, j) => (
          <button
            key={`g-${row.game_id}-${j}`}
            type="button"
            data-testid="check-row"
            onClick={() => go(row)}
            className="rounded border px-2 py-1 text-left text-[11px] hover:bg-muted"
          >
            {rowText(row)}
          </button>
        ))}
        {teams.map((t, j) => (
          <React.Fragment key={`t-${t.registration_id ?? t.team}-${j}`}>
            <button
              type="button"
              data-testid="check-team"
              disabled={!onFocusTeam || !t.registration_id}
              onClick={() => goTeam(t)}
              className="rounded border px-2 py-1 text-left text-[11px] hover:bg-muted disabled:opacity-60"
            >
              Show {t.team}'s games
            </button>
            {teamSummaryHref && t.registration_id && (
              <a
                data-testid="check-team-summary"
                href={teamSummaryHref(String(t.registration_id))}
                className="rounded border px-2 py-1 text-left text-[11px] underline hover:bg-muted"
              >
                {t.team}'s weekend
              </a>
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };


  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col"
        data-testid="schedule-check-panel"
        onCloseAutoFocus={(event) => event.preventDefault()}
      >
        <DialogHeader className="shrink-0">
          <DialogTitle>What is wrong with this schedule</DialogTitle>
          <DialogDescription asChild>
            <div className="space-y-1">
              <div data-testid="check-scope-line">
                {errorCount} {plural(errorCount, 'error')} and {warningCount} {plural(warningCount, 'warning')} across{' '}
                {cellCount} {plural(cellCount, 'cell')} {scopeLabel ? `on ${scopeLabel}` : 'on the board'}.
                {offScopeCount > 0
                  ? ` ${offScopeCount} more ${plural(offScopeCount, 'cell')} on other days.`
                  : ''}{' '}
                Some warnings are about a team's whole weekend and do not belong to one cell.
              </div>
              <div className="font-medium text-foreground" data-testid="check-headline">
                {errorCount > 0
                  ? `${errorCount} ${plural(errorCount, 'error')}. These will go out to teams as they are unless you fix them first.`
                  : 'Nothing is stopping you publishing.'}{' '}
                {warningCount > 0
                  ? `${warningCount} ${plural(warningCount, 'warning')} will not stop you, but ${warningCount === 1 ? 'it is' : 'they are'} worth a look.`
                  : ''}
              </div>

            </div>
          </DialogDescription>
        </DialogHeader>

        {filter && (
          <div className="flex items-center gap-2 text-xs" data-testid="check-filter">
            <span>Showing {filter === 'errors' ? 'errors only' : filter === 'warnings' ? 'warnings only' : (warningGroups.find((w: any) => w?.kind === filter)?.title ?? filter)}.</span>
            <Button size="sm" variant="outline" className="h-6 px-2 text-xs" onClick={() => setFilter(null)}>Show everything</Button>
          </div>
        )}
        <div className="min-h-0 flex-1 overflow-auto pr-1 space-y-4">
          {audit && (
            <section data-testid="integrity-audit">
              <h3 className="mb-2 text-sm font-semibold">
                Integrity audit{' '}
                <span className="font-normal text-muted-foreground">
                  {auditProblemCount(audit) === 0
                    ? '— all eight checks clean'
                    : `— ${auditProblemCount(audit)} to look at`}
                </span>
              </h3>
              <ul className="rounded-md border divide-y text-[12px]">
                {AUDIT_LABELS.map((l) => {
                  const n = Number(audit[l.key] ?? 0);
                  const flag = l.bad && n > 0;
                  return (
                    <li
                      key={l.key}
                      data-testid={`audit-${l.key}`}
                      className={`flex items-center justify-between px-2.5 py-1.5 ${flag ? "text-destructive" : ""}`}
                    >
                      <span className={l.indent ? 'pl-4 text-muted-foreground' : ''}>{l.label}</span>
                      <span className={`tabular-nums ${flag ? 'font-semibold' : ''}`}>{n}</span>
                    </li>
                  );
                })}
              </ul>
              <div className="mt-1 text-[11px] text-muted-foreground">
                Nothing here is repaired automatically. The offending games are named in the list below.
              </div>
            </section>
          )}
          {errorCount === 0 && warningCount === 0 && (
            <div className="flex items-center gap-2 rounded-md border border-green-300 bg-green-50/60 p-3 text-sm text-green-900">
              <CheckCircle2 className="h-4 w-4" /> No problems found on this schedule.
            </div>
          )}

          {showErrors && errorGroups.length > 0 && (
            <section>
              <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-red-800">
                <Circle className="h-3.5 w-3.5 fill-red-600 text-red-700" />
                Errors — these are impossible on the day
              </h3>
              <div className="space-y-2">
                {errorGroups.map((g) => (
                  <div key={g.kind} className="rounded-md border border-red-200 bg-white p-2.5" data-testid="check-error-group">
                    <div className="text-[13px] font-medium text-red-900">
                      {g.title} <span className="font-normal text-muted-foreground">({g.items.length})</span>
                    </div>
                    <ul className="mt-1.5 space-y-2">
                      {g.items.map((e: any, i: number) => (
                        <li key={i} className="text-[12px]">
                          <Finding fkey={`e:${g.kind}:${i}`} title={g.title} text={e.message} finding={e} />
                          <Chips finding={e} />
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </section>
          )}

          {shownWarnings.length > 0 && (
            <section>
              <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-amber-800">
                <AlertTriangle className="h-3.5 w-3.5" />
                Warnings — worth a look, but they do not stop you
              </h3>
              <div className="space-y-2">
                {shownWarnings.map((w: any, i: number) => (
                  <details
                    key={`${w.kind}-${i}`}
                    open
                    className="rounded-md border border-amber-200 bg-white p-2.5"
                    data-testid="check-warning-group"
                    data-warning-kind={w.kind}
                  >
                    <summary className="cursor-pointer text-[13px] font-medium text-amber-900">
                      {w.title} <span className="font-normal text-muted-foreground">({w.count})</span>
                    </summary>
                    {w.note && <div className="mt-1 text-[12px] text-muted-foreground">{w.note}</div>}
                    <ul className="mt-1.5 space-y-1.5">
                      {(w.items ?? []).slice(0, 100).map((it: any, j: number) => (
                        <li key={j} className="text-[12px]">
                          <Finding fkey={`w:${w.kind}:${j}`} title={w.title} text={it.message ?? rowText(it)} finding={it} />
                          <Chips finding={it} />
                          {w.kind === 'back_to_back' && onApproveBackToBack && it.game_a_id && (
                            <Button
                              size="sm" variant="outline" className="mt-1 h-7"
                              data-testid="b2b-approve"
                              disabled={b2bBusy === `a:${it.game_a_id}|${it.game_b_id}|${it.registration_id}`}
                              onClick={() => runB2B(`a:${it.game_a_id}|${it.game_b_id}|${it.registration_id}`, onApproveBackToBack, it)}
                            >
                              Approve
                            </Button>
                          )}
                        </li>
                      ))}
                    </ul>
                  </details>
                ))}
              </div>
            </section>
          )}

          {approvedB2B.length > 0 && (
            <section data-testid="b2b-approved-list" data-warning-kind={warningGroups.some((w) => w.kind === 'back_to_back') ? undefined : 'back_to_back'}>
              <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-foreground">
                <CheckCircle2 className="h-3.5 w-3.5" />
                Approved back-to-backs ({approvedB2B.length} of {Number(check?.back_to_back_total ?? approvedB2B.length)})
              </h3>
              <div className="mb-1 text-[12px] text-muted-foreground">
                Signed off for these exact slots. If either game moves, the approval is dropped and the warning returns.
              </div>
              <ul className="space-y-1.5 rounded-md border bg-card p-2.5">
                {approvedB2B.map((it: any) => (
                  <li key={it.approval_id} className="text-[12px]">
                    <div className="text-foreground">{it.message}</div>
                    <div className="text-muted-foreground">
                      Approved by {it.approved_by_name ?? 'an operator'}
                      {it.approved_at ? ` · ${new Date(it.approved_at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}` : ''}
                    </div>
                    <Chips finding={it} />
                    {onUnapproveBackToBack && (
                      <Button
                        size="sm" variant="ghost" className="mt-1 h-7"
                        data-testid="b2b-unapprove"
                        disabled={b2bBusy === `u:${it.approval_id}`}
                        onClick={() => runB2B(`u:${it.approval_id}`, onUnapproveBackToBack, it)}
                      >
                        Un-approve
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {uncheckedRows.length > 0 && (
            <section data-testid="check-unchecked">
              <h3 className="mb-2 text-sm font-semibold text-muted-foreground">
                Not checked on this event
              </h3>
              <ul className="rounded-md border divide-y text-[12px]">
                {uncheckedRows.map((u: any, i: number) => (
                  <li
                    key={`${u.kind}-${i}`}
                    data-testid={`unchecked-${u.kind}`}
                    className="flex flex-wrap items-center justify-between gap-2 px-2.5 py-1.5 text-muted-foreground"
                  >
                    <span>
                      <span className="text-foreground/80">{u.title}</span>{'  '}
                      {u.note}
                    </span>
                    {setupHref && (
                      <a href={setupHref} className="underline hover:text-foreground">Set it</a>
                    )}
                  </li>
                ))}
              </ul>
              <div className="mt-1 text-[11px] text-muted-foreground">
                These are switched off by their own setting, so they are silent. They are not
                counted in the warning total.
              </div>
            </section>
          )}
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-2 border-t pt-3">
          <Button size="sm" variant="outline" onClick={onRunCheck} disabled={checking}>
            {checking ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="mr-2 h-3.5 w-3.5" />}
            Run check
          </Button>
          {errorCount > 0 && onPublishAnyway && (
            <Button
              size="sm"
              variant="destructive"
              className="ml-auto"
              onClick={() => { onOpenChange(false); onPublishAnyway(); }}
            >
              Publish anyway
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
